import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { LeaveAuditTab } from '@/components/leave/leave-audit-tab'
import { AbilityContext } from '@/components/providers/ability-provider'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { isHttpOrHttpsUrl } from '@/lib/utils'
import { useTRPC } from '@/trpc/react'
import { Can, useAbility } from '@casl/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronLeft, ExternalLink } from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/supervisor/leaves/$leaveRequestId/')({
  component: RouteComponent,
})

const STATUS_BADGE_CLASSNAME: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-900 dark:text-gray-400',
}

function formatDate(iso: string): string {
  return DateTime.fromISO(iso).toFormat('MMM d, yyyy')
}

// submittedAt/approvedAt/rejectedAt are timestamp columns — real Date objects (via
// superjson), not ISO date strings like startDate/endDate, so fromJSDate not fromISO.
function formatDateTime(date: Date): string {
  return DateTime.fromJSDate(date).toFormat('MMM d, yyyy • h:mm a')
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-b-0 border-gray-100 dark:border-gray-800">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right">{value}</span>
    </div>
  )
}

function RouteComponent() {
  const api = useTRPC()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const ability = useAbility(AbilityContext)
  const { leaveRequestId } = Route.useParams()
  const [activeTab, setActiveTab] = useState('details')

  const { data: request, isPending } = useQuery({
    ...api.leave.getLeaveRequestById.queryOptions({ leaveRequestId }),
  })

  // Phase I / Step I1 — HR/admin/supervisor override-cancel. Unlike the employee
  // self-service Cancel button (which only ever appears for a 'pending' request),
  // this one is also offered for 'approved' requests — the DAL reverses the ledger
  // via an offsetting 'adjustment' entry for that case, per cancelLeaveRequestDataAccess.
  const cancelRequestMutation = useMutation({
    ...api.leave.cancelLeaveRequest.mutationOptions({
      onSuccess: () => {
        toast.success('Leave request cancelled')
        queryClient.invalidateQueries({
          queryKey: api.leave.getLeaveRequestById.queryKey({ leaveRequestId }),
        })
        queryClient.invalidateQueries({ queryKey: api.leave.getLeaveRequestHistory.queryKey() })
      },
      onError: (error) => {
        toast.error('Failed to cancel leave request', { description: error.message })
      },
    }),
  })

  return (
    <DashboardShell>
      <Button
        variant="link"
        size="sm"
        onClick={() => navigate({ to: '/dashboard/supervisor/leaves/history' })}
        className="w-fit px-0"
      >
        <ChevronLeft className="mr-1 h-4 w-4" />
        Back to History
      </Button>

      <DashboardHeader heading="Leave Request" text="Full details for this leave request." />

      {isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : !request ? (
        <Card className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
          <CardContent className="pt-6 text-center text-muted-foreground">
            Leave request not found.
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold">
                {request.employee.firstName} {request.employee.lastName}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge className={STATUS_BADGE_CLASSNAME[request.status]} variant="outline">
                  {request.status}
                </Badge>
                {(request.status === 'pending' || request.status === 'approved') && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={cancelRequestMutation.isPending}
                    onClick={() => cancelRequestMutation.mutate({ leaveRequestId })}
                  >
                    Cancel Request
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 px-4">
              <TabsTrigger value="details">Details</TabsTrigger>
              <Can I="read" a="LeaveAuditTab" ability={ability}>
                <TabsTrigger value="audit">Audit Trail</TabsTrigger>
              </Can>
            </TabsList>

            <TabsContent value="details">
              <CardContent>
                <DetailRow
                  label="Leave Type"
                  value={
                    <span className="inline-flex items-center gap-2">
                      {request.leaveType.name}
                      <Badge variant="outline" className="text-[10px]">
                        {request.leaveType.isPaid ? 'Paid' : 'Unpaid'}
                      </Badge>
                    </span>
                  }
                />
                <DetailRow
                  label="Dates"
                  value={
                    request.isHalfDay
                      ? `${formatDate(request.startDate)} (Half-day${request.halfDayPeriod ? `, ${request.halfDayPeriod}` : ''})`
                      : `${formatDate(request.startDate)} - ${formatDate(request.endDate)}`
                  }
                />
                <DetailRow label="Number of Days" value={request.numDays} />
                <DetailRow
                  label="Approver"
                  value={
                    request.approver?.email ?? <span className="text-muted-foreground">—</span>
                  }
                />
                <DetailRow label="Submitted" value={formatDateTime(request.submittedAt)} />
                {request.approvedAt && (
                  <DetailRow label="Approved At" value={formatDateTime(request.approvedAt)} />
                )}
                {request.rejectedAt && (
                  <DetailRow label="Rejected At" value={formatDateTime(request.rejectedAt)} />
                )}
                {request.cancelledAt && (
                  <DetailRow label="Cancelled At" value={formatDateTime(request.cancelledAt)} />
                )}
                <DetailRow
                  label="Reason"
                  value={request.reason ?? <span className="text-muted-foreground">—</span>}
                />
                {request.attachmentUrl && (
                  <DetailRow
                    label="Attachment"
                    value={
                      // cubic review (2026-08-16) / Step E1 — render-side guard: a
                      // pre-existing bad value already in the database (predating the
                      // input-boundary check) must not become a clickable
                      // javascript:/data: link.
                      isHttpOrHttpsUrl(request.attachmentUrl) ? (
                        <a
                          href={request.attachmentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                        >
                          View attachment
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground">Invalid attachment URL</span>
                      )
                    }
                  />
                )}
              </CardContent>
            </TabsContent>

            <Can I="read" a="LeaveAuditTab" ability={ability}>
              <TabsContent value="audit">
                <LeaveAuditTab
                  resourceType="LeaveRequest"
                  resourceId={leaveRequestId}
                  enabled={activeTab === 'audit'}
                />
              </TabsContent>
            </Can>
          </Tabs>
        </Card>
      )}
    </DashboardShell>
  )
}
